/** @jsx jsx */
import { Button, Flex, FolderIcon, FormGroup, Link, UploadIcon } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { useFileUpload } from "@/components/Files/upload/useFileUpload";
import { useFormikField } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { ATTACHMENTS_EXTENSIONS, buildDropzoneAccept } from "@/consts/fileExtensions";
import { Attachment } from "@/data/attachments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { formatMultipleLocations } from "@/data/locations";

import { useApplyWizard } from "../_components/ApplyWizardContext";
import { ApplyFileEntry } from "./ApplyFileEntry";
import { ApplyFilesFormValues } from "./ApplyFilesFormFields";
import { ChooseExistingAttachmentModal } from "./ChooseExistingAttachmentModal";

interface ApplyAttachmentsFormGroupProps {
  formik: FormikProps<ApplyFilesFormValues>;
  originalProfile?: IConsultantProfile;
}

export const ApplyAttachmentsFormGroup: FC<ApplyAttachmentsFormGroupProps> = ({ formik, originalProfile }) => {
  const { t } = useTranslation(["public", "common", "session"]);
  const { sendEvent } = useAnalytics();
  const { jobRequest } = useApplyWizard();

  const [showAddDocumentsButton, setShowAddDocumentsButton] = useState(formik.values.attachments.length === 0);

  const anyProfileAttachments = Boolean(originalProfile?.attachments.length);

  const attachments = formik.values.attachments.filter(i => i.attachment).map(i => i.attachment!);
  const { handleChange } = useFormikField({ name: "attachments" });

  const handleUploaded = useCallback(() => {
    sendEvent({
      type: "apply_job_files",
      id: jobRequest.id,
      name: jobRequest.title!,
      location: formatMultipleLocations(t, jobRequest.locations, jobRequest.locations?.length),
    });
  }, [sendEvent, t, jobRequest]);

  const { state, addDropzoneFiles, addClonedAttachment, deleteFileEntry } = useFileUpload({
    attachments,
    onChange: handleChange,
    onUploaded: handleUploaded,
  });

  const handleExistingAttachmentClick = useCallback(
    async (attachment: Attachment) => {
      addClonedAttachment(attachment);
    },
    [addClonedAttachment],
  );

  const chooseExistingModalState = useDialogState({ animated: true });

  const maxSize = 40;

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: addDropzoneFiles,
    noClick: true,
    preventDropOnDocument: true,
    accept: buildDropzoneAccept(ATTACHMENTS_EXTENSIONS),
    maxSize: maxSize * 1024 * 1024,
    multiple: true,
  });

  return (
    <React.Fragment>
      <FormGroup error={<FormikError name="attachments" />}>
        <Flex flexDirection="column" gap="sm">
          {state.items.map((item, index) => (
            <ApplyFileEntry key={index} item={item} deleteFileEntry={deleteFileEntry} maxSize={maxSize} />
          ))}

          {showAddDocumentsButton ? (
            <Link data-test="add-documents" variant="primary" onClick={() => setShowAddDocumentsButton(false)}>
              {t("session:JOB_REQUESTS.APPLY.WIZARD.ADD_DOCUMENTS")}
            </Link>
          ) : (
            <Flex gap="xs" flexDirection={["column", null, "row"]}>
              {anyProfileAttachments && (
                <div>
                  <Button
                    as={DialogDisclosure}
                    {...chooseExistingModalState}
                    variant="secondary"
                    iconLeft={<FolderIcon />}>
                    {t("session:JOB_REQUESTS.APPLY.WIZARD.CHOOSE_EXISTING")}
                  </Button>
                </div>
              )}

              <div {...getRootProps()}>
                <Button data-test="upload-btn" variant="secondary" iconLeft={<UploadIcon />} onClick={open}>
                  {t("session:JOB_REQUESTS.APPLY.WIZARD.UPLOAD_NEW")}
                </Button>
                <input {...getInputProps()} />
              </div>
            </Flex>
          )}
        </Flex>
      </FormGroup>

      {anyProfileAttachments && (
        <ChooseExistingAttachmentModal
          dialogState={chooseExistingModalState}
          profile={originalProfile!}
          onAttachmentClick={handleExistingAttachmentClick}
        />
      )}
    </React.Fragment>
  );
};
